<script lang="ts" setup>
import {storeToRefs} from 'pinia'
import Dialog from 'primevue/dialog'
import {onMounted, ref, watch} from 'vue'

import TopbarUserForm from '@/components/admin/topbar/TopbarUserForm.vue'
import TutorialContent from '@/components/admin/topbar/TutorialContent.vue'
import HelpForm from '@/components/site/HelpForm.vue'
import LanguageSelector from '@/components/ui/LanguageSelector.vue'
import {useLayout} from '@/layouts/admin/layout.ts'
import {useUiStore} from '@/stores/ui-store'
import {useUserStore} from '@/stores/user-store'
import type {IUser} from '@/types/shared'

const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const uiStore = useUiStore()
const { toggleAdminMenu, showAdminMenu } = uiStore
const { toggleMenu } = useLayout()

const showUserModal = ref<boolean>(false)
const showTutorialModal = ref<boolean>(false)
const showHelpModal = ref<boolean>(false)
const isAdminToggleOn = ref<boolean>(false)

watch(isAdminToggleOn, (newValue: boolean) => {
  handleChangeSwitch(newValue)
})

const handleChangeSwitch = (value: boolean) => {
  toggleAdminMenu(value)
}

onMounted(async () => {
  if (showAdminMenu) {
    isAdminToggleOn.value = true
  }
})
</script>

<template>
  <div>
    <div class="layout-topbar">
      <div class="flex items-center">
        <button class="layout-menu-button layout-topbar-action" @click="toggleMenu">
          <i class="pi pi-bars"></i>
        </button>
      </div>

      <div class="flex justify-center flex-1 lg:justify-start layout-topbar-logo-container">
        <RouterLink class="app-logo" to="/admin">
          <div class="w-36 sm:w-36 mx-auto lg:mx-0">
            <img alt="InvestiBene" src="@/assets/logo.png" class="mx-auto" />
          </div>
        </RouterLink>
      </div>

      <div class="layout-topbar-actions flex items-center gap-2 lg:gap-4">
        <button
            v-styleclass="{
            selector: '@next',
            enterFromClass: 'hidden',
            enterActiveClass: 'animate-scalein',
            leaveToClass: 'hidden',
            leaveActiveClass: 'animate-fadeout',
            hideOnOutsideClick: true,
          }"
            class="layout-topbar-menu-button layout-topbar-action lg:hidden"
        >
          <i class="pi pi-ellipsis-v"></i>
        </button>

        <div class="layout-topbar-menu hidden lg:block">
          <div class="layout-topbar-menu-content">
            <div v-if="user?.admin" class="w-full">
              <ToggleButton
                  v-model="isAdminToggleOn"
                  class="w-full"
                  off-icon="pi pi-eye-slash"
                  on-label="Admin"
                  on-icon="pi pi-eye"
                  off-label="Admin"
              />
            </div>

            <Button class="layout-topbar-action w-full lg:mt-0 mt-1" outlined @click="showUserModal = true">
              <i class="pi pi-user"></i>
              <span>{{ $t('common:profile') }}</span>
            </Button>

            <a class="layout-topbar-action w-full lg:mt-0 mt-1" @click="showTutorialModal = true">
              <i class="pi pi-question-circle"></i>
              <span>{{ $t('common:tutorial') }}</span>
            </a>

            <a class="layout-topbar-action w-full">
              <LanguageSelector />
            </a>
            <Dialog
                v-model:visible="showUserModal"
                :breakpoints="{'960px': '75vw','640px': '90vw'}"
                :close-on-escape="true"
                class="w-96"
                dismissable-mask
                :header="$t('common:user.user_details')"
                modal
            >
              <TopbarUserForm
                  v-if="showUserModal"
                  :data="user as IUser"
                  @close="showUserModal = false"
              />
            </Dialog>
            <Dialog
                v-model:visible="showTutorialModal"
                :breakpoints="{'960px': '75vw','640px': '90vw'}"
                :close-on-escape="true"
                class="w-full max-w-screen-lg"
                dismissable-mask
                modal
            >
              <TutorialContent
                  v-if="showTutorialModal"
                  @close="showTutorialModal = false"
                  @open-contact-us="showHelpModal = true"
              />
            </Dialog>
            <Dialog
                v-model:visible="showHelpModal"
                :breakpoints="{'960px': '75vw','640px': '90vw'}"
                :close-on-escape="true"
                class="w-[35rem]"
                :header="$t('common:help.form-title')"
                dismissable-mask
                modal
            >
              <p class="mb-8">{{ $t('common:help.form-text') }}</p>
              <HelpForm v-if="showHelpModal" :user="user as IUser" @close="showHelpModal = false" />
            </Dialog>
          </div>
        </div>
      </div>
    </div>
    <Button
        rounded
        raised
        class="floating-help-widget fixed bottom-4 right-4 z-50"
        icon="pi pi-question-circle"
        :label="$t('common:help.title')"
        @click="showHelpModal = true"
    />
  </div>
</template>

<style lang="scss" scoped>
.layout-topbar-message {
  position: fixed;
  top: 0;
  z-index: 1000;
}

.floating-help-widget {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  align-items: center;
  cursor: pointer;
  z-index: 1999;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}
</style>
